import { FC, useCallback, useState } from 'react'
import { DragDropContext, DropResult } from 'react-beautiful-dnd'
import { useFormContext } from 'react-hook-form'
import { MobileSearchAssistant, SearchFilter, SearchFilterType } from '@mobileTypes'
import { Box, Divider } from '@mui/material'

import { AvailableFiltersPanel } from './AvailableFiltersPanel'
import { ConfiguredFiltersPanel } from './ConfiguredFiltersPanel'
import { FilterConfigDialog } from './FilterConfigDialog'

export const AdvancedFiltersConfigTab: FC = () => {
  const { watch, setValue } = useFormContext<MobileSearchAssistant>()

  const filters = watch('filters') || []

  const [configDialogState, setConfigDialogState] = useState<{
    open: boolean
    mode: 'create' | 'edit'
    filterType?: SearchFilterType
    destination?: { droppableId: string; index: number }
    editIndex?: number
    initialData?: Partial<SearchFilter>
  }>({
    open: false,
    mode: 'create',
  })

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      const { source, destination, draggableId } = result

      if (!destination) return

      // Check if dropped back to the same place
      if (source.droppableId === destination.droppableId && source.index === destination.index) {
        return
      }

      // Adding new filter from Available Filters
      if (draggableId.startsWith('NEW:')) {
        const filterType = draggableId.split(':')[1] as SearchFilterType

        setConfigDialogState({
          open: true,
          mode: 'create',
          filterType,
          destination,
        })

        return
      }

      // Reordering within FILTERS
      if (source.droppableId === 'FILTERS' && destination.droppableId === 'FILTERS') {
        const updatedFilters = [...filters]
        const [removed] = updatedFilters.splice(source.index, 1)
        updatedFilters.splice(destination.index, 0, removed)

        const finalFilters = updatedFilters.map((f, idx) => ({ ...f, order: idx }))
        setValue('filters', finalFilters, { shouldDirty: true })
      }
    },
    [filters, setValue]
  )

  const handleEditFilter = useCallback(
    (index: number) => {
      const filter = filters[index]
      if (filter) {
        setConfigDialogState({
          open: true,
          mode: 'edit',
          editIndex: index,
          initialData: filter,
          filterType: filter.type,
        })
      }
    },
    [filters]
  )

  const handleDeleteFilter = useCallback(
    (index: number) => {
      const updatedFilters = filters.filter((_, idx) => idx !== index)
      const finalFilters = updatedFilters.map((f, idx) => ({ ...f, order: idx }))
      setValue('filters', finalFilters, { shouldDirty: true })
    },
    [filters, setValue]
  )

  const handleSaveFilterConfig = useCallback(
    (filterConfig: SearchFilter) => {
      const { mode, destination, editIndex } = configDialogState

      if (mode === 'create' && destination) {
        const updatedFilters = [...filters]
        updatedFilters.splice(destination.index, 0, filterConfig)
        const finalFilters = updatedFilters.map((f, idx) => ({ ...f, order: idx }))
        setValue('filters', finalFilters, { shouldDirty: true })
      } else if (mode === 'edit' && editIndex !== undefined) {
        const updatedFilters = [...filters]
        updatedFilters[editIndex] = filterConfig
        setValue('filters', updatedFilters, { shouldDirty: true })
      }

      setConfigDialogState({ open: false, mode: 'create' })
    },
    [configDialogState, filters, setValue]
  )

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Box sx={{ display: 'flex', gap: 2, height: '600px' }}>
        <Box sx={{ flex: 1 }}>
          <AvailableFiltersPanel />
        </Box>

        <Divider orientation='vertical' flexItem />

        <Box sx={{ flex: 2 }}>
          <ConfiguredFiltersPanel
            filters={filters}
            onEdit={handleEditFilter}
            onDelete={handleDeleteFilter}
          />
        </Box>
      </Box>

      <FilterConfigDialog
        open={configDialogState.open}
        mode={configDialogState.mode}
        filterType={configDialogState.filterType}
        initialData={configDialogState.initialData}
        onClose={() => setConfigDialogState({ open: false, mode: 'create' })}
        onSave={handleSaveFilterConfig}
      />
    </DragDropContext>
  )
}





